import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface Tenant {
  slug: string;
  displayName: string;
  status: string;
}

interface EventRow {
  tenantSlug: string;
  messageId: string;
  eventType: string;
  receivedAt: string;
}

function buildQueryString(input: {
  tenant: string;
  types: string[];
  from: string;
  to: string;
  cursor?: string;
}) {
  const params = new URLSearchParams();
  if (input.tenant) params.set('tenant', input.tenant);
  if (input.types.length) params.set('type', input.types.join(','));
  if (input.from) params.set('from', input.from);
  if (input.to) params.set('to', input.to);
  if (input.cursor) params.set('cursor', input.cursor);
  const query = params.toString();
  return query ? `/events?${query}` : '/events';
}

const EVENT_TYPES = ['Bounce', 'Complaint', 'Delivery', 'Send'] as const;

const EVENT_COLORS: Record<string, string> = {
  Send: 'bg-indigo-50 text-indigo-700',
  Delivery: 'bg-emerald-50 text-emerald-700',
  Bounce: 'bg-red-50 text-red-700',
  Complaint: 'bg-amber-50 text-amber-700',
};

interface EventFilters {
  tenant: string;
  types: string[];
  from: string;
  to: string;
}

const EMPTY_FILTERS: EventFilters = { tenant: '', types: [], from: '', to: '' };

const inputClass =
  'w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20';

export function Events() {
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const data = await apiFetch('/tenants');
      return (data.items ?? []) as Tenant[];
    },
  });

  const [draftFilters, setDraftFilters] = useState<EventFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<EventFilters>(EMPTY_FILTERS);

  const eventsQuery = useInfiniteQuery({
    queryKey: ['events', appliedFilters],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      return apiFetch(buildQueryString({ ...appliedFilters, cursor: pageParam })) as Promise<{
        items: EventRow[];
        nextCursor?: string;
      }>;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const events = eventsQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [];

  function toggleType(type: string) {
    setDraftFilters((current) => ({
      ...current,
      types: current.types.includes(type)
        ? current.types.filter((item) => item !== type)
        : [...current.types, type],
    }));
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900">Events</h1>
        <p className="mt-0.5 text-sm text-zinc-500">SES reputation events across all tenants.</p>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400" htmlFor="events-tenant">
              Tenant
            </label>
            <select
              id="events-tenant"
              className={inputClass}
              value={draftFilters.tenant}
              onChange={(event) =>
                setDraftFilters((current) => ({ ...current, tenant: event.target.value }))
              }
            >
              <option value="">All tenants</option>
              {(tenantsQuery.data ?? []).map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <p className="block text-xs font-medium uppercase tracking-wider text-zinc-400">Type</p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {EVENT_TYPES.map((type) => (
                <label key={type} className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500/20"
                    checked={draftFilters.types.includes(type)}
                    onChange={() => toggleType(type)}
                  />
                  <span className="text-zinc-700">{type}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400" htmlFor="events-from">
              From
            </label>
            <input
              id="events-from"
              type="date"
              className={inputClass}
              value={draftFilters.from}
              onChange={(event) =>
                setDraftFilters((current) => ({ ...current, from: event.target.value }))
              }
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-400" htmlFor="events-to">
              To
            </label>
            <input
              id="events-to"
              type="date"
              className={inputClass}
              value={draftFilters.to}
              onChange={(event) =>
                setDraftFilters((current) => ({ ...current, to: event.target.value }))
              }
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-4">
          <button
            className="text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
            type="button"
            onClick={() => {
              setDraftFilters(EMPTY_FILTERS);
              setAppliedFilters(EMPTY_FILTERS);
            }}
          >
            Clear
          </button>
          <button
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
            type="button"
            onClick={() => setAppliedFilters(draftFilters)}
          >
            Apply Filters
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-zinc-100 text-sm">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/50">
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Message ID</th>
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Tenant</th>
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Type</th>
              <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-400">Received</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {events.map((event) => (
              <tr key={`${event.messageId}-${event.receivedAt}`} className="hover:bg-zinc-50/70 transition-colors">
                <td className="px-5 py-3.5">
                  <Link
                    className="font-mono text-xs text-indigo-600 hover:underline underline-offset-2"
                    to={`/events/${event.messageId}`}
                  >
                    {event.messageId}
                  </Link>
                </td>
                <td className="px-5 py-3.5 text-sm text-zinc-600">{event.tenantSlug}</td>
                <td className="px-5 py-3.5">
                  <span
                    className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      EVENT_COLORS[event.eventType] ?? 'bg-zinc-100 text-zinc-600'
                    }`}
                  >
                    {event.eventType}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-xs tabular-nums text-zinc-400">{event.receivedAt}</td>
              </tr>
            ))}
            {!events.length && !eventsQuery.isLoading ? (
              <tr>
                <td className="px-5 py-12 text-center text-sm text-zinc-400" colSpan={4}>
                  No events found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {eventsQuery.hasNextPage ? (
        <button
          className="rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-600 shadow-sm hover:bg-zinc-50 transition-colors"
          type="button"
          onClick={() => void eventsQuery.fetchNextPage()}
        >
          Load More
        </button>
      ) : null}
    </div>
  );
}
